import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Video, Upload, RefreshCw, Trash2, Copy, ShieldCheck, AlertTriangle, Download, Pencil, Captions } from "lucide-react";
import { toast } from "sonner";

const SUBS_LABELS = {
  ready: { label: "Subtítulos ES listos", cls: "bg-green-500/20 text-green-400" },
  preparing: { label: "Generando subtítulos", cls: "bg-blue-500/20 text-blue-300" },
  errored: { label: "Error en subtítulos", cls: "bg-red-500/20 text-red-300" },
  no_audio: { label: "Sin audio detectado", cls: "bg-white/10 text-white/50" },
};

const STATUS_LABELS = {
  ready: { label: "Listo", cls: "bg-green-500/20 text-green-400" },
  deleted: { label: "Eliminado de Mux", cls: "bg-red-500/20 text-red-300" },
  processing: { label: "Procesando en Mux", cls: "bg-blue-500/20 text-blue-300" },
  preparing: { label: "Procesando en Mux", cls: "bg-blue-500/20 text-blue-300" },
  waiting_for_upload: { label: "Esperando archivo", cls: "bg-white/10 text-white/60" },
  errored: { label: "Error", cls: "bg-red-500/20 text-red-300" },
  cancelled: { label: "Cancelado", cls: "bg-white/10 text-white/50" },
};

export const MuxVideoLibrary = () => {
  const { API } = useAuth();
  const [configured, setConfigured] = useState(true);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [subsLoading, setSubsLoading] = useState(null);
  const [broken, setBroken] = useState([]);
  const [fixSelection, setFixSelection] = useState({});

  const importExisting = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/admin/mux/import-existing`);
      toast.success(`${res.data.imported} video(s) importado(s) de tu cuenta Mux`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/mux/videos`);
      setConfigured(res.data.configured);
      setVideos(res.data.videos);
      loadBroken();
    } catch (e) {
      toast.error("Error al cargar la biblioteca de videos");
    } finally {
      setLoading(false);
    }
  };

  const upload = async () => {
    if (!title.trim()) return toast.error("Ponle un título al video");
    if (!file) return toast.error("Selecciona el archivo de video");
    setUploading(true);
    setProgress(0);
    try {
      const res = await axios.post(`${API}/admin/mux/upload-url`, { title });
      const { upload_url } = res.data;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", upload_url);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)));
        xhr.onerror = () => reject(new Error("Error de red al subir"));
        xhr.send(file);
      });

      toast.success("Video subido. Mux lo está procesando, puede tardar unos minutos.");
      setTitle("");
      setFile(null);
      setProgress(0);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al subir el video");
    } finally {
      setUploading(false);
    }
  };

  const generateSubs = async (v) => {
    setSubsLoading(v.id);
    try {
      const res = await axios.post(`${API}/admin/mux/videos/${v.id}/generate-subtitles`);
      toast.success(res.data.message || "Subtítulos en proceso. Suelen tardar unos minutos.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron generar los subtítulos");
    } finally {
      setSubsLoading(null);
    }
  };

  const rename = async (v) => {
    const title = window.prompt("Nuevo nombre del video:", v.title);
    if (!title || !title.trim()) return;
    try {
      await axios.put(`${API}/admin/mux/videos/${v.id}`, { title });
      toast.success("Nombre actualizado");
      load();
    } catch (e) {
      toast.error("No se pudo renombrar");
    }
  };

  const refresh = async (id) => {
    try {
      await axios.post(`${API}/admin/mux/videos/${id}/refresh`);
      load();
    } catch (e) {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`¿Eliminar "${v.title}" de Mux? Las lecciones que lo usen quedarán sin video.`)) return;
    try {
      await axios.delete(`${API}/admin/mux/videos/${v.id}`);
      toast.success("Video eliminado");
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const copyId = (pid) => {
    navigator.clipboard.writeText(pid);
    toast.success("Playback ID copiado");
  };

  const loadBroken = async () => {
    try {
      const res = await axios.get(`${API}/admin/mux/broken-lessons`);
      setBroken(res.data.broken || []);
    } catch (e) {
      setBroken([]);
    }
  };

  const fixLesson = async (item) => {
    const playbackId = fixSelection[`${item.course_id}:${item.lesson_id}`];
    if (!playbackId) return toast.error("Elige el video correcto para esta clase");
    try {
      await axios.post(`${API}/admin/mux/fix-lesson`, {
        course_id: item.course_id,
        lesson_id: item.lesson_id,
        playback_id: playbackId,
      });
      toast.success("Clase reparada: el video ya se puede ver");
      loadBroken();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reparar la clase");
    }
  };

  return (
    <div className="space-y-6" data-testid="mux-library">
      {broken.length > 0 && (
        <Card className="bg-red-500/10 border-red-500/30" data-testid="mux-broken-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Clases con video no disponible ({broken.length})
            </CardTitle>
            <CardDescription className="text-white/70">
              Estas clases apuntan a un video que ya no existe en Mux. Elige el video correcto y pulsa Reparar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {broken.map((b) => {
              const key = `${b.course_id}:${b.lesson_id}`;
              return (
                <div key={key} className="p-3 bg-black/30 rounded-lg border border-white/10 flex flex-col lg:flex-row lg:items-center gap-3 justify-between" data-testid={`broken-lesson-${b.lesson_id}`}>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{b.course_title} → {b.lesson_title}</p>
                    <p className="text-white/50 text-xs">{b.reason}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      value={fixSelection[key] || ""}
                      onChange={(e) => setFixSelection({ ...fixSelection, [key]: e.target.value })}
                      className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                      data-testid={`broken-select-${b.lesson_id}`}
                    >
                      <option value="">Elegir video...</option>
                      {videos.filter((v) => v.playback_id && v.status === "ready").map((v) => (
                        <option key={v.id} value={v.playback_id} className="bg-black">{v.title}</option>
                      ))}
                    </select>
                    <Button onClick={() => fixLesson(b)} className="bg-[#E50914] hover:bg-[#E50914]/90 text-white" data-testid={`broken-fix-${b.lesson_id}`}>
                      Reparar
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {!configured && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="py-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-semibold">Mux todavía no está conectado</p>
              <p className="text-white/70 text-sm">
                Necesito 4 datos de tu cuenta de Mux: Token ID, Token Secret, Signing Key ID y Private Key.
                Pásamelos en el chat y los configuro de forma segura en el servidor.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card/50 backdrop-blur-md border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#E50914]" />
            Subir video a Mux
          </CardTitle>          <CardDescription className="text-white/60">
            Los videos se guardan con reproducción firmada: solo se pueden ver dentro de ContaFLIX, con enlaces
            temporales y sin botón de descarga.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white mb-2 block">Título del video</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Cómo calcular el precio de venta - Clase 1"
                className="bg-white/5 border-white/10 text-white"
                data-testid="mux-title-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Archivo de video</Label>
              <Input
                type="file"
                accept="video/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="bg-white/5 border-white/10 text-white file:text-white"
                data-testid="mux-file-input"
              />
            </div>
          </div>
          {uploading && (
            <div>
              <Progress value={progress} className="h-2" />
              <p className="text-white/60 text-sm mt-1">Subiendo... {progress}%</p>
            </div>
          )}
          <Button
            onClick={upload}
            disabled={uploading || !configured}
            className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
            data-testid="mux-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Subiendo..." : "Subir a Mux"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card/50 backdrop-blur-md border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-[#E50914]" />
              Biblioteca de videos ({videos.length})
            </CardTitle>
            <CardDescription className="text-white/60">
              Usa el Playback ID en la lección (tipo de video: Mux protegido).
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={importExisting} disabled={importing} className="border-white/20 text-white hover:bg-white/10" data-testid="mux-import-btn">
              <Download className="w-4 h-4 mr-2" />
              {importing ? "Importando..." : "Importar de Mux"}
            </Button>
            <Button variant="outline" onClick={load} className="border-white/20 text-white hover:bg-white/10" data-testid="mux-reload-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-white/60 text-center py-8">Cargando...</p>
          ) : videos.length === 0 ? (
            <p className="text-white/60 text-center py-8">Aún no has subido videos a Mux</p>
          ) : (
            <div className="space-y-3">
              {videos.map((v) => {
                const st = STATUS_LABELS[v.status] || { label: v.status, cls: "bg-white/10 text-white/60" };
                return (
                  <div key={v.id} className="p-4 bg-white/5 rounded-lg border border-white/10" data-testid={`mux-video-${v.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-semibold truncate">{v.title}</p>
                          <Badge className={`${st.cls} border-0`}>{st.label}</Badge>
                          <Badge className="bg-emerald-500/15 text-emerald-300 border-0">
                            <ShieldCheck className="w-3 h-3 mr-1" /> Firmado
                          </Badge>
                          {SUBS_LABELS[v.subtitles_status] && (
                            <Badge className={`${SUBS_LABELS[v.subtitles_status].cls} border-0`} data-testid={`mux-subs-status-${v.id}`}>
                              <Captions className="w-3 h-3 mr-1" /> {SUBS_LABELS[v.subtitles_status].label}
                            </Badge>
                          )}
                        </div>
                        {v.playback_id ? (
                          <p className="text-white/50 text-xs mt-1 font-mono truncate">{v.playback_id}</p>
                        ) : (
                          <p className="text-white/40 text-xs mt-1">El Playback ID aparece cuando Mux termina de procesar</p>
                        )}
                        {v.duration_seconds ? (
                          <p className="text-white/40 text-xs">Duración: {Math.round(v.duration_seconds / 60)} min</p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {v.playback_id && (
                          <Button variant="outline" onClick={() => copyId(v.playback_id)} className="border-white/20 text-white hover:bg-white/10" data-testid={`mux-copy-${v.id}`}>
                            <Copy className="w-4 h-4 mr-1" /> Copiar ID
                          </Button>
                        )}
                        {v.status === "ready" && v.subtitles_status !== "ready" && v.subtitles_status !== "preparing" && (
                          <Button
                            onClick={() => generateSubs(v)}
                            disabled={subsLoading === v.id}
                            className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                            data-testid={`mux-subs-${v.id}`}
                          >
                            <Captions className="w-4 h-4 mr-1" />
                            {subsLoading === v.id ? "Generando..." : "Generar subtítulos"}
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => rename(v)} className="border-white/20 text-white hover:bg-white/10" data-testid={`mux-rename-${v.id}`}>
                          <Pencil className="w-4 h-4 mr-1" /> Renombrar
                        </Button>
                        <Button variant="outline" onClick={() => refresh(v.id)} className="border-white/20 text-white hover:bg-white/10" data-testid={`mux-refresh-${v.id}`}>
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" onClick={() => remove(v)} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`mux-delete-${v.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MuxVideoLibrary;
